import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { managementAPI } from '../../services/api';
import api from '../../services/api';
import '../../App.css';

function CreateUser() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState('normal');
  const [parentUserId, setParentUserId] = useState('');
  
  // For dependent account parent selection
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    // Load users for parent selection (in case creating dependent)
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await api.get('/management/users?status=approved');
      const allUsers = response.data.data.users || [];
      // Filter out dependent accounts (can't be parents)
      const normalUsers = allUsers.filter(u => u.accountType !== 'dependent');
      setUsers(normalUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validation
    if (!email || !password || !name) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (accountType === 'dependent' && !parentUserId) {
      setError('Please select a parent user for dependent account');
      return;
    }
    
    try {
      setLoading(true);
      
      const userData = {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        accountType,
      };
      
      if (accountType === 'dependent') {
        userData.parentUserId = parentUserId;
      }
      
      await managementAPI.createUser(userData);
      
      setSuccess(`User ${email} created successfully!`);
      
      // Clear form
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setName('');
      setAccountType('normal');
      setParentUserId('');
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
      
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>Create New User</h1>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/admin')}
        >
          ← Back to Dashboard
        </button>
      </div>

      {success && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1rem', 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          border: '1px solid #c3e6cb',
          borderRadius: '4px'
        }}>
          {success}
        </div>
      )}

      {error && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1rem', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          border: '1px solid #f5c6cb',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              required
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label>Confirm Password *</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              required
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label>Account Type *</label>
            <select 
              value={accountType} 
              onChange={(e) => {
                setAccountType(e.target.value);
                if (e.target.value === 'normal') {
                  setParentUserId('');
                }
              }}
            >
              <option value="normal">Normal (Independent)</option>
              <option value="dependent">Dependent (Family Account)</option>
            </select>
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
              {accountType === 'normal' 
                ? 'Independent user with their own login'
                : 'Dependent account managed by a parent user'}
            </small>
          </div>

          {accountType === 'dependent' && (
            <div className="form-group">
              <label>Parent User *</label>
              {loadingUsers ? (
                <p>Loading users...</p>
              ) : (
                <select 
                  value={parentUserId} 
                  onChange={(e) => setParentUserId(e.target.value)}
                  required
                >
                  <option value="">-- Select Parent User --</option>
                  {users.map(user => (
                    <option key={user.userId} value={user.userId}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              )}
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                The parent user who will manage this dependent account
              </small>
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
            <button 
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/admin')}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <style jsx="true">{`
        .form-container {
          max-width: 600px;
          background: white;
          padding: 2rem;
          border-radius: 8px;
          border: 2px solid #e0e0e0;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #333;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #5c7cfa;
        }
      `}</style>
    </div>
  );
}

export default CreateUser;
